/**
 * CalendarFormDialog
 *
 * Dialog reutilizável para criar ou editar um calendário do usuário.
 * Suporta modo "create" (sem `calendar`) e "edit" (com `calendar` preenchido).
 *
 * Campos: nome, cor, descrição, visibilidade, primário, ativo (apenas edit).
 *
 * RELEASE:
 *   2026-08-04 v1.3.0 - Seleção de origem também na edição e opções completas
 *                       da integração Google embutidas no modal (a aba
 *                       "Integrações" das configurações foi removida).
 *   2026-08-04 v1.2.0 - Seleção de origem (sistema x Google) e conexão OAuth
 *                       do Google Agenda direto no modal; aviso de vínculo
 *                       Google redesenhado.
 *   2026-06-25 v1.1.0 - Layout padronizado (header premium + footer).
 *   2026-06-24 v1.0.0 - Versão inicial (CRUD de calendários no sidebar/Configurações)

 */

import { useEffect, useState } from 'react';
import { Calendar as CalendarIcon, CalendarCog, CalendarPlus, Loader2 } from 'lucide-react';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';
import { useCalendarIntegration } from '@/hooks/useSchedulingEventTypes';
import { GoogleIntegrationInlinePanel } from './GoogleIntegrationInlinePanel';


import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCalendars, type UserCalendar } from '@/hooks/useCalendars';
import { toast } from 'sonner';


/** Paleta sugerida de cores para o calendário */
const COLOR_PRESETS = [
  '#3B82F6', '#22C55E', '#EAB308', '#F97316',
  '#EF4444', '#A855F7', '#EC4899', '#14B8A6',
  '#0EA5E9', '#64748B',
];

interface CalendarFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, ativa modo edição */
  calendar?: UserCalendar | null;
}

export function CalendarFormDialog({
  open,
  onOpenChange,
  calendar,
}: CalendarFormDialogProps) {
  const isEdit = !!calendar;
  const { create, update, isCreating, isUpdating } = useCalendars();
  const saving = isCreating || isUpdating;

  // Estado do formulário
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'tenant' | 'shared'>('private');
  const [isPrimary, setIsPrimary] = useState(false);
  const [isActive, setIsActive] = useState(true);
  /** v1.2.0: origem escolhida na criação (sistema x Google Agenda). */
  const [source, setSource] = useState<'local' | 'google'>('local');

  // Integração Google do usuário (para conectar direto daqui)
  const { integration: googleIntegration, isConnected: isGoogleConnected } =
    useCalendarIntegration();



  // Reidrata o formulário quando abre / muda o calendário em edição
  useEffect(() => {
    if (!open) return;
    if (isEdit && calendar) {
      setName(calendar.name);
      setColor(calendar.color);
      setDescription(calendar.description ?? '');
      setVisibility(calendar.visibility);
      setIsPrimary(calendar.is_primary);
      setIsActive(calendar.is_active);
      // v1.3.0: origem derivada do vínculo Google já existente
      setSource(calendar.google_integration_id ? 'google' : 'local');
    } else {
      setName('');
      setColor('#3B82F6');
      setDescription('');
      setVisibility('private');
      setIsPrimary(false);
      setIsActive(true);
      setSource('local');

    }
  }, [open, isEdit, calendar]);


  /** Submete create ou update conforme o modo */
  const handleSubmit = async () => {
    // Validação simples no front (sanidade) — backend revalida via RPC
    const trimmed = name.trim();
    if (trimmed.length < 1 || trimmed.length > 100) {
      toast.error('Informe um nome entre 1 e 100 caracteres');
      return;
    }
    if (description.length > 500) {
      toast.error('Descrição deve ter no máximo 500 caracteres');
      return;
    }

    try {
      if (isEdit && calendar) {
        await update({
          id: calendar.id,
          name: trimmed,
          color,
          description: description.trim() || null,
          visibility,
          is_primary: isPrimary,
          is_active: isActive,
        });
        toast.success('Calendário atualizado');
      } else {
        await create({
          name: trimmed,
          color,
          description: description.trim() || null,
          visibility,
          is_primary: isPrimary,
        });
      }
      onOpenChange(false);
    } catch {
      // Toast de erro já é tratado pelo hook
    }
  };

  // Em calendários vinculados ao Google, evitamos alterar visibilidade/cor
  // de forma destrutiva — apenas nome/descrição/primary continuam habilitados.
  const isGoogleLinked = !!calendar?.google_integration_id;
  const canManage = !calendar || calendar.user_permission === 'manage';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col sm:rounded-lg">
        {/* Header premium padronizado */}
        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent shrink-0">
          <div className="flex items-start gap-3 min-w-0 pr-8">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              {isEdit ? (
                <CalendarCog className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              ) : (
                <CalendarPlus className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="truncate">
                {isEdit ? 'Editar calendário' : 'Novo calendário'}
              </DialogTitle>
              <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                {isEdit
                  ? 'Atualize os dados deste calendário. Permissões são gerenciadas separadamente.'
                  : 'Crie um calendário manual para organizar seus eventos.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo rolável */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">

          {/* ------------------------------------------------------------------
              Origem do calendário (v1.2.0)
              Traz a conexão com o Google para dentro da criação/edição, em vez
              de deixá-la escondida na aba "Integrações".
             ------------------------------------------------------------------ */}
          <div className="space-y-1.5">
            <Label>Origem</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSource('local')}
                disabled={isGoogleLinked}
                className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  source === 'local'
                    ? 'border-primary bg-primary/5'
                    : 'border-border/50 hover:bg-muted/40'
                }`}
              >
                <CalendarIcon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">Calendário do sistema</span>
                  <span className="block text-xs text-muted-foreground">
                    Eventos ficam somente aqui.
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => setSource('google')}
                className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors ${
                  source === 'google'
                    ? 'border-primary bg-primary/5'
                    : 'border-border/50 hover:bg-muted/40'
                }`}
              >
                <img src={googleCalendarIcon} alt="" className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">Google Agenda</span>
                  <span className="block text-xs text-muted-foreground">
                    Sincroniza nos dois sentidos.
                  </span>
                </span>
              </button>
            </div>

            {isGoogleLinked && (
              <p className="text-xs text-muted-foreground">
                Este calendário já está vinculado ao Google Agenda. Para desvinculá-lo,
                desconecte ou exclua a conta Google abaixo.
              </p>
            )}

            {/* Opções da integração Google (antiga aba "Integrações") */}
            {source === 'google' && (
              <div className="rounded-lg border border-border/50 bg-muted/20 p-3 space-y-3">
                {!isEdit && !isGoogleConnected && (
                  <p className="text-xs text-muted-foreground">
                    Conecte sua conta Google para criar automaticamente um calendário
                    sincronizado nos dois sentidos.
                  </p>
                )}

                <GoogleIntegrationInlinePanel compact handleOAuthRedirect={false} />

                {!isEdit && isGoogleConnected && googleIntegration && (
                  <p className="text-xs text-muted-foreground">
                    O calendário desta conta já existe na sua lista (nome "Google – {googleIntegration.provider_email ?? 'conta'}").
                    Feche este modal e edite-o para ajustar nome, cor e visibilidade.
                  </p>
                )}
              </div>
            )}
          </div>




          {/* Nome */}
          <div className="space-y-1.5">
            <Label htmlFor="cal-name">Nome</Label>
            <Input
              id="cal-name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 100))}
              placeholder="Ex: Pessoal, Trabalho, Reuniões"
              disabled={!canManage}
              maxLength={100}
            />
          </div>

          {/* Cor (paleta) */}
          <div className="space-y-1.5">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  disabled={!canManage}
                  className={`h-7 w-7 rounded-md border-2 transition-all ${
                    color === c
                      ? 'border-foreground scale-110'
                      : 'border-border/50 hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                  aria-label={`Cor ${c}`}
                />
              ))}
              <Input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={!canManage}
                className="h-7 w-12 p-0.5 cursor-pointer"
                aria-label="Cor personalizada"
              />
            </div>
          </div>

          {/* Descrição */}
          <div className="space-y-1.5">
            <Label htmlFor="cal-desc">Descrição (opcional)</Label>
            <Textarea
              id="cal-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              placeholder="Para quê serve este calendário?"
              disabled={!canManage}
              maxLength={500}
              rows={2}
            />
          </div>

          {/* Visibilidade */}
          <div className="space-y-1.5">
            <Label>Visibilidade</Label>
            <Select
              value={visibility}
              onValueChange={(v) => setVisibility(v as typeof visibility)}
              disabled={!canManage}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Privado (somente eu)</SelectItem>
                <SelectItem value="tenant">Tenant (toda a organização vê)</SelectItem>
                <SelectItem value="shared">Compartilhado (matriz de permissões)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toggles */}
          <div className="flex items-center justify-between rounded-md border border-border/50 p-3">
            <div className="space-y-0.5">
              <Label htmlFor="cal-primary" className="text-sm">Marcar como primário</Label>
              <p className="text-xs text-muted-foreground">
                Será o calendário padrão ao criar novos eventos.
              </p>
            </div>
            <Switch
              id="cal-primary"
              checked={isPrimary}
              onCheckedChange={setIsPrimary}
              disabled={!canManage}
            />
          </div>

          {isEdit && (
            <div className="flex items-center justify-between rounded-md border border-border/50 p-3">
              <div className="space-y-0.5">
                <Label htmlFor="cal-active" className="text-sm">Ativo</Label>
                <p className="text-xs text-muted-foreground">
                  Quando desativado, o calendário fica oculto sem ser excluído.
                </p>
              </div>
              <Switch
                id="cal-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={!canManage}
              />
            </div>
          )}

        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {!isEdit && source === 'google' ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canManage || saving || (!isEdit && source === 'google')}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Salvar' : 'Criar calendário'}
          </Button>
        </div>


      </DialogContent>
    </Dialog>
  );
}
